import React, { ReactNode } from 'react';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import ParkIcon from '@mui/icons-material/Park';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { FormInputConfig } from '~/types/residentManagement';
import { uriRegex } from '~/helpers/helpers';
import { PlaceCategory } from '~/api/places';

export const categoryOptions: {
  label: string;
  value: PlaceCategory;
}[] = [
  { label: 'Food', value: 'food' },
  { label: 'Coffee', value: 'coffee' },
  { label: 'Outdoors', value: 'outdoor' },
  { label: 'Shopping', value: 'shop' }
];

export const categoryLabel = (category: PlaceCategory): string =>
  categoryOptions.find((c) => c.value === category)?.label ?? category;

// Narrows an arbitrary select value to a known PlaceCategory (or '' for none).
export const toCategory = (value?: string): PlaceCategory | '' =>
  categoryOptions.find((c) => c.value === value)?.value ?? '';

export const categoryIcon = (category: PlaceCategory): ReactNode => {
  switch (category) {
    case 'food':
      return <RestaurantIcon fontSize='small' />;
    case 'coffee':
      return <LocalCafeIcon fontSize='small' />;
    case 'outdoor':
      return <ParkIcon fontSize='small' />;
    case 'shop':
      return <ShoppingBagIcon fontSize='small' />;
    default:
      return null;
  }
};

const fullWidth = { inputStyles: { width: '100%' } };

// Field configs consumed by the shared FormField renderer in the Add-a-place modal.
export const nameField: FormInputConfig = {
  id: 'name',
  label: 'Place name',
  inputType: 'text',
  isRequired: true,
  placeholder: 'e.g. Groundwork Coffee',
  styles: fullWidth
};

export const mapsLinkField: FormInputConfig = {
  id: 'mapsLink',
  label: 'Google Maps link',
  inputType: 'text',
  isRequired: true,
  placeholder: 'Paste a Google Maps link to this place',
  pattern: uriRegex(),
  patternErrorMsg: 'Please enter a valid url',
  styles: fullWidth
};

export const streetField: FormInputConfig = {
  id: 'street',
  label: 'Street address',
  inputType: 'text',
  isRequired: true,
  placeholder: 'e.g. 88 Oak Ave',
  styles: fullWidth
};

// City + Zip render side by side on the address row.
export const cityField: FormInputConfig = {
  id: 'city',
  label: 'City',
  inputType: 'text',
  isRequired: true,
  placeholder: 'e.g. Brooklyn',
  styles: fullWidth
};

export const zipField: FormInputConfig = {
  id: 'zip',
  label: 'ZIP',
  inputType: 'text',
  isRequired: true,
  placeholder: 'e.g. 11201',
  styles: fullWidth
};

export const categoryField: FormInputConfig = {
  id: 'category',
  label: 'Category',
  inputType: 'chip-list',
  isRequired: true,
  selectOptions: categoryOptions
};

export const descriptionField: FormInputConfig = {
  id: 'description',
  label: 'Comment',
  inputType: 'textarea',
  isRequired: false,
  placeholder: 'Add a note about this place...',
  styles: { inputStyles: { width: '100%', height: '90px' } }
};
